/*
  bc-wishlist.js : la liste d'envie de Laudaris. Sans app, sans backend.

  ARCHITECTURE
  - Stockage : localStorage, clé versionnée `bc:liste-envie:v1`, une liste de
    handles produit. try/catch sur CHAQUE accès : en navigation privée ou avec
    le stockage bloqué, l'accesseur lui-même peut jeter. Un repli en mémoire
    prend alors le relais (perdu au rechargement, mais rien ne casse).
  - Boutons cœur : `[data-bc-wl-toggle="handle"]`, partout (cartes, fiche,
    page partagée). Un SEUL écouteur en délégation sur document : le thème
    REMPLACE des nœuds entiers (morph du panier, filtres de collection,
    recherche prédictive), un écouteur posé sur le bouton serait emporté.
    Même leçon que l'attache rejouable de snippets/cart-drawer.liquid.
  - Compteur du header : `[data-bc-wl-compteur]`, repeint à chaque changement.
  - Page liste d'envie : racine `[data-bc-wl-page]`, produits chargés par
    `/products/HANDLE.js`, cellules clonées depuis un <template> de la section
    (tout le texte client vit dans le Liquid, jamais dans ce fichier).
  - Liste partagée : paramètre `?liste=h1,h2` lu au chargement de la page.
  - Ajout au panier : la mécanique du thème (<cart-upsell-component>,
    assets/cart-upsell.js), rien de réécrit ici. Le tiroir s'ouvre et se met
    à jour tout seul via CartLinesUpdateEvent.

  ⚠️ Ce fichier ne contient AUCUN texte destiné au client : les libellés
     viennent des attributs data-libelle-* posés par le Liquid.
*/
(() => {
  'use strict';

  /* Le script peut être inclus deux fois (theme.liquid + section, chargement
     défensif) : on ne s'initialise qu'une seule fois. */
  if (window.__bcListeEnvie) return;
  window.__bcListeEnvie = true;

  const CLE = 'bc:liste-envie:v1';
  const PLAFOND = 100;
  /* Un handle Shopify : minuscules, chiffres, tirets. Sert aussi de filtre de
     sécurité pour le paramètre ?liste= (rien d'autre ne passe). */
  const MOTIF_HANDLE = /^[a-z0-9][a-z0-9-]*$/;

  /* ------------------------------------------------------------------ */
  /* Stockage                                                            */
  /* ------------------------------------------------------------------ */

  let secours = null; /* repli en mémoire quand localStorage est inaccessible */

  function lire() {
    let brut = null;
    try {
      brut = window.localStorage.getItem(CLE);
    } catch (e) {
      brut = secours;
    }
    if (brut === null && secours !== null) brut = secours;
    if (!brut) return [];
    try {
      const d = JSON.parse(brut);
      if (d && Array.isArray(d.handles)) {
        return d.handles
          .filter((h) => typeof h === 'string' && MOTIF_HANDLE.test(h))
          .slice(0, PLAFOND);
      }
    } catch (e) {
      /* contenu corrompu : on repart d'une liste vide plutôt que de casser */
    }
    return [];
  }

  function ecrire(handles) {
    const brut = JSON.stringify({ v: 1, handles: handles.slice(0, PLAFOND), maj: Date.now() });
    secours = brut;
    try {
      window.localStorage.setItem(CLE, brut);
    } catch (e) {
      /* quota plein ou mode privé : le repli mémoire suffit pour la visite */
    }
    try {
      document.dispatchEvent(new CustomEvent('bc:liste-envie:change'));
    } catch (e) {
      /* jamais bloquant */
    }
  }

  function basculer(handle) {
    const liste = lire();
    const i = liste.indexOf(handle);
    if (i === -1) {
      liste.push(handle);
      /* au plafond, le plus ancien sort : un ajout ne doit jamais échouer en silence */
      while (liste.length > PLAFOND) liste.shift();
    } else {
      liste.splice(i, 1);
    }
    ecrire(liste);
    return i === -1; /* true si le produit vient d'être AJOUTÉ */
  }

  function retirer(handle) {
    const liste = lire();
    const i = liste.indexOf(handle);
    if (i !== -1) {
      liste.splice(i, 1);
      ecrire(liste);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Peinture : cœurs, compteurs, libellés                               */
  /* peindre() n'écrit un attribut QUE s'il diffère de l'état voulu :    */
  /* idempotente, donc appelable sans retenue (morphs, rafales).         */
  /* ------------------------------------------------------------------ */

  function peindre() {
    const liste = lire();
    const dans = Object.create(null);
    for (const h of liste) dans[h] = true;

    document.querySelectorAll('[data-bc-wl-toggle]').forEach((b) => {
      const h = b.getAttribute('data-bc-wl-toggle');
      const actif = !!dans[h];
      const attendu = actif ? 'true' : 'false';
      if (b.getAttribute('aria-pressed') !== attendu) b.setAttribute('aria-pressed', attendu);

      const libelle = actif
        ? b.getAttribute('data-libelle-retrait')
        : b.getAttribute('data-libelle-ajout');
      if (libelle && b.getAttribute('aria-label') !== libelle) b.setAttribute('aria-label', libelle);

      /* libellé visible éventuel (bouton « Ajouter à ma liste » de la page partagée) */
      const texte = b.querySelector('[data-bc-wl-texte]');
      if (texte) {
        const voulu = actif ? b.getAttribute('data-texte-retrait') : b.getAttribute('data-texte-ajout');
        if (voulu && texte.textContent !== voulu) texte.textContent = voulu;
      }
    });

    const n = liste.length;
    document.querySelectorAll('[data-bc-wl-compteur]').forEach((c) => {
      const affiche = n > 99 ? '99+' : String(n);
      if (c.textContent !== affiche) c.textContent = affiche;
      if (c.hidden !== (n === 0)) c.hidden = n === 0;
    });

    /* le lien du header annonce le nombre au lecteur d'écran */
    document.querySelectorAll('[data-bc-wl-lien]').forEach((a) => {
      let libelle = a.getAttribute('data-libelle-vide') || '';
      if (n === 1) libelle = a.getAttribute('data-libelle-un') || libelle;
      else if (n > 1) libelle = (a.getAttribute('data-libelle-n') || '').replace('{n}', String(n)) || libelle;
      if (libelle && a.getAttribute('aria-label') !== libelle) a.setAttribute('aria-label', libelle);
    });
  }

  /* ------------------------------------------------------------------ */
  /* La page liste d'envie                                               */
  /* ------------------------------------------------------------------ */

  let page = null;

  function initPage() {
    const racine = document.querySelector('[data-bc-wl-page]');
    if (!racine) return null;

    const el = (sel) => racine.querySelector(sel);
    const refs = {
      bandeau: el('[data-bc-wl-bandeau]'),
      lienMienne: el('[data-bc-wl-lien-mienne]'),
      barre: el('[data-bc-wl-barre]'),
      nombre: el('[data-bc-wl-nombre]'),
      copieOk: el('[data-bc-wl-copie-ok]'),
      copieManuel: el('[data-bc-wl-copie-manuel]'),
      attente: el('[data-bc-wl-attente]'),
      grille: el('[data-bc-wl-grille]'),
      erreur: el('[data-bc-wl-erreur]'),
      vide: el('[data-bc-wl-vide]'),
      videMien: el('[data-bc-wl-vide-mien]'),
      videPartage: el('[data-bc-wl-vide-partage]'),
      gabarit: el('template[data-bc-wl-gabarit]'),
    };
    if (!refs.grille || !refs.gabarit) return null;

    /* --- liste partagée : ?liste=h1,h2 ------------------------------- */
    let partage = null;
    try {
      const brut = new URLSearchParams(window.location.search).get('liste');
      if (brut !== null) {
        const vus = Object.create(null);
        const hs = [];
        for (const morceau of brut.split(',')) {
          const h = morceau.trim().toLowerCase();
          if (MOTIF_HANDLE.test(h) && !vus[h]) {
            vus[h] = true;
            hs.push(h);
          }
          if (hs.length >= PLAFOND) break;
        }
        partage = hs; /* possiblement vide : on reste en mode partagé, avec son état vide */
      }
    } catch (e) {
      partage = null;
    }

    /* --- format des prix --------------------------------------------- */
    let fmt = null;
    try {
      fmt = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
    } catch (e) {
      fmt = null;
    }
    function prixTexte(cents) {
      const v = cents / 100;
      if (fmt) {
        try {
          return fmt.format(v);
        } catch (e) {
          /* on retombe sur le format manuel */
        }
      }
      return v.toFixed(2).replace('.', ',') + ' €';
    }

    /* --- affichage des états ------------------------------------------ */
    function montrer(etat) {
      if (refs.attente) refs.attente.hidden = etat !== 'attente';
      refs.grille.hidden = etat !== 'grille';
      if (refs.erreur) refs.erreur.hidden = etat !== 'erreur';
      if (refs.vide) refs.vide.hidden = etat !== 'vide';
      /* la barre (compte + copier) n'existe que sur SA liste, jamais sur une liste partagée */
      if (refs.barre) refs.barre.hidden = !(etat === 'grille' && !partage);
      if (refs.videMien) refs.videMien.hidden = !!partage;
      if (refs.videPartage) refs.videPartage.hidden = !partage;
      /* 11/09 : l'en-tete change sur une liste partagee (« MA liste » devient
         « la liste d'un proche ») : bascule generique des paires marquees */
      document.querySelectorAll('[data-bc-wl-mien]').forEach(function (e) { e.hidden = !!partage; });
      document.querySelectorAll('[data-bc-wl-partage]').forEach(function (e) { e.hidden = !partage; });
    }

    function majNombre(n) {
      if (!refs.nombre) return;
      const un = refs.nombre.getAttribute('data-libelle-un') || '{n}';
      const plusieurs = refs.nombre.getAttribute('data-libelle-n') || '{n}';
      refs.nombre.textContent = (n === 1 ? un : plusieurs).replace('{n}', String(n));
    }

    /* --- chargement d'un produit -------------------------------------- */
    async function chargerProduit(handle) {
      try {
        const rep = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' },
        });
        if (rep.status === 404) return { handle, disparu: true };
        if (!rep.ok) return null; /* erreur serveur : on ne juge pas, on n'affiche pas */
        const produit = await rep.json();
        return { handle, produit };
      } catch (e) {
        return null; /* erreur réseau */
      }
    }

    /* --- construction d'une cellule ------------------------------------ */
    function construireCellule(handle, p) {
      const frag = refs.gabarit.content.cloneNode(true);
      const cellule = frag.querySelector('[data-bc-wl-cellule]');
      if (!cellule) return frag;
      cellule.setAttribute('data-bc-wl-cellule', handle);
      const q = (sel) => frag.querySelector(sel);
      const titre = p.title || handle;

      const url = '/products/' + encodeURIComponent(handle);
      const lien = q('[data-ref="lien"]');
      const lienTexte = q('[data-ref="lien-texte"]');
      if (lien) lien.href = url;
      if (lienTexte) lienTexte.href = url;

      /* image carrée : la première image, servie en 480 px par le CDN Shopify */
      const img = q('[data-ref="img"]');
      if (img) {
        const src = p.featured_image || (Array.isArray(p.images) && p.images[0]) || '';
        if (src) {
          img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=480';
          img.alt = titre;
        } else {
          img.remove();
        }
      }

      /* le titre maison : « Requête SEO - Prénom » devient surtitre + nom,
         la même règle que bc-product-title côté Liquid */
      const morceaux = String(titre).split(' - ');
      const cat = q('[data-ref="cat"]');
      const nom = q('[data-ref="nom"]');
      if (nom) {
        if (morceaux.length > 1 && cat) {
          cat.textContent = morceaux[0];
          cat.hidden = false;
          nom.textContent = morceaux.slice(1).join(' - ');
        } else {
          nom.textContent = titre;
        }
      }

      /* prix, avec le prix barré quand le produit est en promotion */
      const prix = q('[data-ref="prix"]');
      if (prix) prix.textContent = prixTexte(p.price);
      const barre = q('[data-ref="prix-barre"]');
      if (barre && p.compare_at_price && p.compare_at_price > p.price) {
        barre.textContent = prixTexte(p.compare_at_price);
        barre.hidden = false;
      }

      /* ajout au panier : on remplit le <cart-upsell-component> du gabarit,
         le module du thème fait tout le reste (événements, morph du tiroir) */
      const upsell = q('[data-ref="upsell"]');
      const ajouter = q('[data-ref="ajouter"]');
      const variantes = Array.isArray(p.variants) ? p.variants : [];
      const variante = variantes.find((v) => v && v.available) || null;
      if (upsell && ajouter) {
        if (p.available && variante) {
          upsell.setAttribute('data-variant-id', String(variante.id));
          const modele = ajouter.getAttribute('data-libelle-ajouter') || '';
          if (modele) ajouter.setAttribute('aria-label', modele.replace('{titre}', titre));
        } else {
          ajouter.disabled = true;
          const epuise = ajouter.getAttribute('data-libelle-epuise');
          if (epuise) ajouter.textContent = epuise;
        }
      }

      /* le bouton du bas : « Retirer » sur SA liste, « Ajouter à ma liste »
         quand on regarde la liste d'un proche */
      const bRetirer = q('[data-ref="retirer"]');
      const bGarder = q('[data-ref="garder"]');
      if (partage) {
        if (bRetirer) bRetirer.remove();
        if (bGarder) {
          bGarder.setAttribute('data-bc-wl-toggle', handle);
          const ajout = bGarder.getAttribute('data-libelle-ajout') || '';
          const retrait = bGarder.getAttribute('data-libelle-retrait') || '';
          if (ajout) bGarder.setAttribute('data-libelle-ajout', ajout.replace('{titre}', titre));
          if (retrait) bGarder.setAttribute('data-libelle-retrait', retrait.replace('{titre}', titre));
        }
      } else {
        if (bGarder) bGarder.remove();
        if (bRetirer) {
          bRetirer.setAttribute('data-bc-wl-retirer', handle);
          const modele = bRetirer.getAttribute('data-libelle-retirer') || '';
          if (modele) bRetirer.setAttribute('aria-label', modele.replace('{titre}', titre));
        }
      }

      return frag;
    }

    /* --- rendu de la page ----------------------------------------------- */
    let jeton = 0;

    async function rendre() {
      const mien = ++jeton;
      const handles = partage ? partage : lire();

      if (refs.bandeau) refs.bandeau.hidden = !partage;
      if (refs.lienMienne) refs.lienMienne.href = window.location.pathname;

      if (!handles.length) {
        montrer('vide');
        return;
      }

      montrer('attente');
      const resultats = await Promise.all(handles.map(chargerProduit));
      if (mien !== jeton) return; /* un rendu plus récent est parti entre-temps */

      const vivants = [];
      const disparus = [];
      let pannes = 0;
      for (const r of resultats) {
        if (r === null) {
          pannes += 1;
          continue;
        }
        if (r.disparu) {
          disparus.push(r.handle);
          continue;
        }
        vivants.push(r);
      }

      /* un produit retiré de la boutique (404) sort de la liste tout seul,
         mais UNIQUEMENT de la sienne : on ne touche jamais à une liste partagée */
      if (!partage && disparus.length) {
        ecrire(lire().filter((h) => disparus.indexOf(h) === -1));
      }

      if (!vivants.length) {
        /* si tout a échoué à cause du réseau, dire « liste vide » serait faux */
        montrer(pannes > 0 ? 'erreur' : 'vide');
        return;
      }

      const frag = document.createDocumentFragment();
      for (const r of vivants) frag.appendChild(construireCellule(r.handle, r.produit));
      refs.grille.textContent = '';
      refs.grille.appendChild(frag);
      majNombre(vivants.length);
      montrer('grille');
      peindre();
    }

    /* --- retirer une cellule (bouton « Retirer ») ------------------------ */
    function retirerCellule(bouton) {
      const cellule = bouton.closest('[data-bc-wl-cellule]');
      if (!cellule) return;
      cellule.classList.add('lenvie__cellule--sortie');
      /* minuterie fixe plutôt qu'animationend : une animation peut ne jamais
         finir (onglet en arrière-plan), la cellule doit sortir quand même */
      setTimeout(() => {
        cellule.remove();
        const restants = refs.grille.querySelectorAll('[data-bc-wl-cellule]').length;
        if (!restants) montrer('vide');
        else majNombre(restants);
      }, 240);
    }

    /* --- « Copier ma liste » --------------------------------------------- */
    let minuteurCopie = null;

    async function copier() {
      const handles = lire();
      if (!handles.length) return;
      const url = window.location.origin + window.location.pathname + '?liste=' + handles.join(',');

      /* 11/09, Val : partage vers les reseaux sociaux : la feuille de partage
         native (WhatsApp, Messages, Instagram...) quand elle existe, la copie
         du lien en repli. AbortError = l'utilisateur a ferme la feuille : on ne
         copie pas par-dessus. */
      if (navigator.share) {
        try {
          await navigator.share({ title: "Ma liste d'envie Laudaris", url: url });
          return;
        } catch (e) {
          if (e && e.name === 'AbortError') return;
        }
      }

      let ok = false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          ok = true;
        }
      } catch (e) {
        ok = false;
      }
      if (!ok) {
        /* repli pour les navigateurs anciens : zone de texte + execCommand */
        try {
          const zone = document.createElement('textarea');
          zone.value = url;
          zone.setAttribute('readonly', '');
          zone.style.position = 'fixed';
          zone.style.opacity = '0';
          document.body.appendChild(zone);
          zone.select();
          ok = document.execCommand('copy');
          zone.remove();
        } catch (e) {
          ok = false;
        }
      }

      if (ok) {
        if (refs.copieOk) {
          refs.copieOk.hidden = false;
          clearTimeout(minuteurCopie);
          minuteurCopie = setTimeout(() => {
            refs.copieOk.hidden = true;
          }, 3500);
        }
      } else if (refs.copieManuel) {
        /* dernier recours : montrer le lien, sélectionné, à copier à la main */
        refs.copieManuel.value = url;
        refs.copieManuel.hidden = false;
        try {
          refs.copieManuel.focus();
          refs.copieManuel.select();
        } catch (e) {
          /* la sélection est un confort, pas une condition */
        }
      }
    }

    return { partage: !!partage, rendre, retirerCellule, copier };
  }

  /* ------------------------------------------------------------------ */
  /* Écouteurs globaux                                                   */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (e) => {
    const cible = e.target instanceof Element ? e.target : null;
    if (!cible) return;

    const coeur = cible.closest('[data-bc-wl-toggle]');
    if (coeur) {
      /* sur les cartes, le cœur est posé au-dessus du lien produit : on coupe
         court pour que le clic n'ouvre pas la fiche */
      e.preventDefault();
      e.stopPropagation();
      const h = coeur.getAttribute('data-bc-wl-toggle');
      if (!h || !MOTIF_HANDLE.test(h)) return;
      const ajoute = basculer(h);
      peindre();
      if (ajoute) {
        coeur.classList.add('est-anime');
        setTimeout(() => coeur.classList.remove('est-anime'), 500);
      }
      return;
    }

    const bRetirer = cible.closest('[data-bc-wl-retirer]');
    if (bRetirer) {
      const h = bRetirer.getAttribute('data-bc-wl-retirer');
      if (h) {
        retirer(h);
        peindre();
        if (page) page.retirerCellule(bRetirer);
      }
      return;
    }

    if (cible.closest('[data-bc-wl-copier]') && page) page.copier();
  });

  /* changement programmatique (autre composant, autre partie du code) */
  document.addEventListener('bc:liste-envie:change', peindre);

  /* autre onglet : l'événement storage ne se déclenche QUE dans les autres onglets */
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== CLE) return;
    peindre();
    if (page && !page.partage) page.rendre();
  });

  /* retour par le cache arrière/avant du navigateur : l'état peut avoir changé */
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    peindre();
    if (page && !page.partage) page.rendre();
  });

  /* Le thème remplace des nœuds entiers : les cœurs re-rendus par le serveur
     reviennent à l'état neutre, on les repeint. childList seulement : peindre
     n'écrit que des attributs, l'observateur ne se réveille donc jamais sur
     ses propres écritures, aucune boucle possible. */
  let minuteurPeinture = null;
  const observateur = new MutationObserver(() => {
    clearTimeout(minuteurPeinture);
    minuteurPeinture = setTimeout(peindre, 180);
  });

  function demarrer() {
    page = initPage();
    if (page) page.rendre();
    peindre();
    try {
      observateur.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
      /* sans observateur, les cœurs marchent quand même, ils ne survivent
         juste pas à un morph : dégradation acceptable */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();
